import Vue from 'vue';
import {AXIOS} from "@/lib/http-common";
import Component from 'vue-class-component';
import User from "@/components/users/model/User";
import {UserRoleWithPrefix} from "@/components/users/model/UserRoleWithPrefix";
import {UserRole} from "@/components/users/model/UserRole";
import UserActionResultSender from "@/components/application/UserActionResultSender";

interface UserWithRepeatPassword extends User {
  id: string;
  password: string;
  repeatPassword: string;
  username: string;
  role: UserRole;
}

@Component({
  computed: {
    UserRoleWithPrefix() {
      return UserRoleWithPrefix;
    }
  }
})
export default class Users extends Vue {
  public users: User[] = [];
  public roles = Object.values(UserRole).sort();
  public rolesWithoutDev = this.roles.filter(r => r !== UserRole.DEV.toString());
  public loading: boolean = false;
  public isRequired = [(value: boolean) => {
    if (value) {
      return true;
    }

    return 'Required';
  }];

  public userToAdd: UserWithRepeatPassword = this.getInitializedUser();
  public addUserDialogOpened: boolean = false;
  public isAddUserFormValid: any = null;

  public userToEdit: UserWithRepeatPassword = this.getInitializedUser();
  public editUserDialogOpened: boolean = false;
  public isEditUserFormValid: any = null;
  public shouldModifyPassword: boolean = false;

  public userToDelete: User | null = null;
  public deleteUserDialogOpened: boolean = false;

  get addUserPasswordsMatchRequired() {
    return this.passwordsMatchRequired(this.userToAdd);
  }

  get editUserPasswordsMatchRequired() {
    return this.passwordsMatchRequired(this.userToEdit);
  }

  get usersTH() {
    return [
      {text: this.$t('users.tableHeaders.username'), value: "username"},
      {text: this.$t('users.tableHeaders.password'), value: "password"},
      {text: this.$t('users.tableHeaders.role'), value: "role"},
      {text: this.$t('users.tableHeaders.action'), value: "action", sortable: false}
    ];
  }

  public mounted() {
    this.getUsersFromBackend();
  }

  public getUsersFromBackend() {
    this.loading = true;
    AXIOS.get("/user")
        .then((message) => {
          this.users = message.data;
        })
        .finally(() => {
          this.loading = false;
        });
  }

  public getInitializedUser(): UserWithRepeatPassword {
    return {id: '', username: '', password: '', repeatPassword: '', role: UserRole.USER};
  }

  public openAddUserDialog() {
    this.addUserDialogOpened = true;
  }

  public openEditUserDialog(user: User) {
    this.editUserDialogOpened = true;
    this.userToEdit.id = user.id;
    this.userToEdit.username = user.username;
    this.userToEdit.role = user.role;
  }

  public openDeleteUserDialog(user: User) {
    this.deleteUserDialogOpened = true;
    this.userToDelete = user;
  }

  public closeAddUserDialog() {
    this.resetValidationAddForm();
    this.userToAdd = this.getInitializedUser();
    this.addUserDialogOpened = false;
    this.getUsersFromBackend();
  }

  public closeEditUserDialog() {
    this.editUserDialogOpened = false;
    this.resetValidationEditForm();
    this.userToEdit = this.getInitializedUser();
    this.shouldModifyPassword = false;
    this.getUsersFromBackend();
  }

  public closeDeleteUserDialog() {
    this.deleteUserDialogOpened = false;
    if (this.userToDelete) {
      this.getUsersFromBackend();
    }
    this.userToDelete = null;
  }

  public resetValidationAddForm() {
    (this.$refs.addUserForm as Vue & { resetValidation: () => boolean }).resetValidation();
  }

  public resetValidationEditForm() {
    (this.$refs.editUserForm as Vue & { resetValidation: () => boolean }).resetValidation();
  }

  public addUser() {
    this.loading = true;

    const userToSend: UserWithRepeatPassword = this.getInitializedUser();
    userToSend.password = this.encryptPassword(this.userToAdd.password);
    userToSend.username = this.userToAdd.username;
    userToSend.role = this.userToAdd.role;

    AXIOS.post("/user", userToSend)
        .then(() => {
          this.closeAddUserDialog();
          UserActionResultSender.showSuccessFeedback('users.success.userAdded');
        })
        .catch(error => {
          UserActionResultSender.showErrorFeedback(error.response.data.errorCode);
        })
        .finally(() => {
          this.loading = false;
        });
  }

  public editUser() {
    this.loading = true;

    const userToSend: UserWithRepeatPassword = this.getInitializedUser();
    userToSend.id = this.userToEdit.id;
    userToSend.username = this.userToEdit.username;
    userToSend.password = this.encryptPassword(this.userToEdit.password);
    userToSend.role = this.userToEdit.role;
    AXIOS.put("/user", userToSend)
        .then(() => {
          this.closeEditUserDialog();
          UserActionResultSender.showSuccessFeedback('users.success.userEdited');
        })
        .catch(error => {
          UserActionResultSender.showErrorFeedback(error.response.data.errorCode);
        })
        .finally(() => {
          this.loading = false;
        });
  }

  public deleteUser() {
    if (this.userToDelete == null) {
      return;
    }

    this.loading = true;
    AXIOS.delete("/user/" + this.userToDelete.username)
        .then(() => {
          this.closeDeleteUserDialog();
          UserActionResultSender.showSuccessFeedback('users.success.userDeleted');
        })
        .catch(error => {
          UserActionResultSender.showErrorFeedback(error.response.data.errorCode);
        })
        .finally(() => {
          this.loading = false;
        });
  }

  public isLastDevOrAdmin(userToCheck: User): boolean {
    if (userToCheck.role === UserRole.USER) {
      return false;
    }

    const usersWithRoleLeft = this.users.filter(user => user.role === userToCheck.role).length;

    return usersWithRoleLeft <= 1;
  }

  public encryptPassword(password: string): string {
    const crypto = require('crypto');
    return crypto.createHash('md5').update(password).digest("hex");
  }

  private passwordsMatchRequired(user: UserWithRepeatPassword) {
    if (!user.repeatPassword) {
      return ['Required'];
    }

    if ((user && user.password && user.repeatPassword && user.password === user.repeatPassword)) {
      return [true];
    }

    return ['Passwords do not match'];
  }
}
